import { DOCUMENT } from "@angular/common";
import { Injectable, inject, signal } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";

import {
  COMPANY_LANGUAGE,
  DEFAULT_PEOPLE_MATE_LANGUAGE,
  LANGUAGES_OPTIONS,
} from "src/app/shared/constants/languages";
import { Language } from "src/app/shared/interfaces/language.interface";

import { BROWSER_STORAGE_KEYS, BrowserStorageService } from "./browser-storage.service";

@Injectable({ providedIn: "root" })
export class TranslationService {
  private readonly translate = inject(TranslateService);
  private readonly browserStorage = inject(BrowserStorageService);
  private readonly document = inject(DOCUMENT);

  private readonly _activeLang = signal<Language>(DEFAULT_PEOPLE_MATE_LANGUAGE);
  readonly activeLang = this._activeLang.asReadonly();

  init(): void {
    this.translate.addLangs(LANGUAGES_OPTIONS);
    this.applyLanguage(this.resolveInitialLanguage());
  }

  switchLang(language: Language): boolean {
    if (this._activeLang().code === language.code) {
      return false;
    }

    this.applyLanguage(language);
    return true;
  }

  private applyLanguage(language: Language): void {
    this._activeLang.set(language);
    this.translate.use(language.code);
    this.browserStorage.set("local", BROWSER_STORAGE_KEYS.LANG, language.code);
    this.applyHtmlLanguage(language.code);
  }

  private resolveInitialLanguage(): Language {
    const stored = this.browserStorage.get("local", BROWSER_STORAGE_KEYS.LANG) as string | null;

    if (stored) {
      return COMPANY_LANGUAGE(stored);
    }

    return DEFAULT_PEOPLE_MATE_LANGUAGE;
  }

  private applyHtmlLanguage(code: string): void {
    const html = this.document.documentElement;
    html.lang = code;
    html.dir = code === "ar" ? "rtl" : "ltr";
  }
}
